// Helpers for the help-center category hierarchy (parent_category_id self-reference).
// Kept as pure functions so they can be reused by the store getter, the category form
// (eligible-parent options) and the article category picker, and unit-tested in isolation.
// Nesting depth is unlimited; the only rule is a category cannot become its own descendant.

// Build a nested tree from a flat category list. Each returned node is a shallow copy
// with a `children` array. A node whose parent_category_id does not resolve within the
// list is treated as a root (defensive against partial payloads / other-locale parents).
// Cycle-safe: a visited set stops a malformed parent chain from recursing forever.
export function buildCategoryTree(categories = []) {
  const byId = new Map(categories.map(category => [category.id, category]));
  const childrenByParent = new Map();

  categories.forEach(category => {
    const parentId = category.parent_category_id;
    const isRoot = !parentId || !byId.has(parentId);
    const key = isRoot ? null : parentId;
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    childrenByParent.get(key).push(category);
  });

  const attach = (node, visited) => {
    if (visited.has(node.id)) return { ...node, children: [] };
    visited.add(node.id);
    const children = (childrenByParent.get(node.id) || []).map(child =>
      attach(child, visited)
    );
    return { ...node, children };
  };

  return (childrenByParent.get(null) || []).map(root =>
    attach(root, new Set())
  );
}

// Depth-first flatten of a tree, annotating each node with its 0-based `level`.
export function flattenCategoryTree(tree = [], level = 0) {
  return tree.reduce((acc, node) => {
    acc.push({ ...node, level });
    if (node.children?.length) {
      acc.push(...flattenCategoryTree(node.children, level + 1));
    }
    return acc;
  }, []);
}

// Ids of every descendant of `id` (excluding the node itself).
export function getDescendantIds(tree = [], id = null) {
  // flattened copies keep their `children` refs, so we can locate the node anywhere
  const target = flattenCategoryTree(tree).find(node => node.id === id);
  if (!target) return [];

  const collect = nodes =>
    nodes.reduce(
      (acc, node) => acc.concat(node.id, collect(node.children || [])),
      []
    );

  return collect(target.children || []);
}

// Options a category may legally be re-parented to, matching the backend rule:
// exclude only the node itself and its descendants (nesting depth is unlimited).
// `categories` is the flat list; `editingId` is null/undefined when creating.
export function getEligibleParentCategories(categories = [], editingId = null) {
  const tree = buildCategoryTree(categories);
  const flat = flattenCategoryTree(tree);

  const descendantIds = editingId ? getDescendantIds(tree, editingId) : [];
  const excluded = new Set([editingId, ...descendantIds]);

  return flat.filter(node => !excluded.has(node.id));
}
